import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import api from '../../services/apiClient';
import { getCoursePassportLinks } from '../../config/careerPassports';
import './CareerDashboard.css';

const getRecommendationPassports = (recommendation) => (
  getCoursePassportLinks({
    programme_name: recommendation.course_name,
    passport_links: recommendation.passport_links,
    passport_alignment_reviewed: recommendation.passport_alignment_reviewed
  })
);

const CareerDashboard = () => {
  const location = useLocation();
  const [assessmentStatus, setAssessmentStatus] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [savedComparisons, setSavedComparisons] = useState([]);
  const [loading, setLoading] = useState(true);

  const passportSummary = Array.from(
    recommendations.reduce((summary, recommendation) => {
      getRecommendationPassports(recommendation).forEach(passport => {
        const existing = summary.get(passport.id);
        summary.set(passport.id, {
          ...passport,
          courseCount: (existing?.courseCount || 0) + 1
        });
      });
      return summary;
    }, new Map()).values()
  );

  useEffect(() => {
    loadDashboardData();
  }, []);

  // Reload data when returning from assessment or recommendations page
  useEffect(() => {
    if (location.state?.assessmentCompleted || location.state?.recommendationsGenerated) {

      loadDashboardData();
    }
  }, [location.state]);

  const loadDashboardData = async () => {
    try {


      // Load assessment status
      const { data: statusData } = await api.get('/career/assessment-status/');

      setAssessmentStatus(statusData);

      // Load recommendations if assessment is complete (use the fresh data)
      if (statusData?.assessment_complete) {


        const { data: recData } = await api.get('/career/recommendations/');


        // Handle paginated response
        let recommendationsArray = [];
        if (recData && typeof recData === 'object' && recData.results && Array.isArray(recData.results)) {
          recommendationsArray = recData.results;
        } else if (Array.isArray(recData)) {
          recommendationsArray = recData;
        }


        setRecommendations(recommendationsArray.slice(0, 3)); // Show top 3
      } else {

      }

      // Load saved comparisons
      try {
        const { data: compData } = await api.get('/career/comparisons/');
        setSavedComparisons((Array.isArray(compData) ? compData : compData.results || []).slice(0, 3));
      } catch (e) {

      }

    } catch (error) {

    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="career-dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading your career dashboard...</p>
      </div>
    );
  }

  return (
    <div className="career-dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          <div className="header-text">
            <h1>🎯 Career Guidance Dashboard</h1>
            <p>Your personalized career development hub. Discover training programs that match your skills and goals.</p>
          </div>
          <div className="header-overview" aria-label="Career guidance overview">
            <div className="header-overview-item">
              <span className="overview-label">Assessment</span>
              <strong>{assessmentStatus?.assessment_complete ? 'Complete' : 'In progress'}</strong>
            </div>
            <div className="header-overview-item">
              <span className="overview-label">Recommendations</span>
              <strong>{assessmentStatus?.recommendations_count || 0}</strong>
            </div>
          </div>
          <div className="header-actions">
            <button
              onClick={loadDashboardData}
              className="btn-refresh"
              disabled={loading}
            >
              {loading ? '🔄 Loading...' : '🔄 Refresh'}
            </button>
          </div>
        </div>
      </div>

      <section className="passport-pathways" aria-labelledby="passport-pathways-title">
        <div className="passport-pathways-heading">
          <div>
            <span className="section-kicker">Your direction</span>
            <h2 id="passport-pathways-title">Passport pathways</h2>
            <p>These pathways connect your recommendations to the skills and occupations they support.</p>
          </div>
          <Link to="/courses" className="pathway-link">Explore all courses <span aria-hidden="true">→</span></Link>
        </div>

        {passportSummary.length > 0 ? (
          <div className="passport-pathway-list">
            {passportSummary.slice(0, 4).map(passport => (
              <div className={`passport-pathway passport-pathway--${passport.tone}`} key={passport.id}>
                <div className="passport-pathway-topline">
                  <span className="passport-pathway-mark" aria-hidden="true"></span>
                  <strong>{passport.title}</strong>
                </div>
                <span className="passport-pathway-count">
                  {passport.courseCount} recommended {passport.courseCount === 1 ? 'course' : 'courses'}
                </span>
                <p>{passport.coverage}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="passport-pathway-empty">
            <strong>Complete your assessment to unlock your Passport pathways.</strong>
            <span>Your recommendations will show which skills and occupations each pathway supports.</span>
          </div>
        )}
      </section>

      <div className="dashboard-grid">
        {/* Assessment Status Card */}
        <div className="dashboard-card assessment-card">
          <div className="card-header">
            <h2>📋 Career Assessment</h2>
            <div className="assessment-status">
              {assessmentStatus?.assessment_complete ? (
                <span className="status-badge complete">✅ Complete</span>
              ) : (
                <span className="status-badge incomplete">⏳ Incomplete</span>
              )}
            </div>
          </div>
          
          <div className="card-content">
            {assessmentStatus?.assessment_complete ? (
              <div className="assessment-summary">
                <div className="summary-stats">
                  <div className="stat">
                    <span className="stat-number">{assessmentStatus.recommendations_count}</span>
                    <span className="stat-label">Recommendations</span>
                  </div>
                  <div className="stat">
                    <span className="stat-number">
                      {assessmentStatus.has_skills_assessment ? '✓' : '✗'}
                    </span>
                    <span className="stat-label">Skills Assessed</span>
                  </div>
                  <div className="stat">
                    <span className="stat-number">
                      {assessmentStatus.has_interests_assessment ? '✓' : '✗'}
                    </span>
                    <span className="stat-label">Interests Mapped</span>
                  </div>
                </div>
                <p>Your assessment is complete! We've generated personalized course recommendations based on your profile.</p>
              </div>
            ) : (
              <div className="assessment-prompt">
                <p>Take our comprehensive career assessment to get personalized training recommendations.</p>
                <ul>
                  <li>📚 Evaluate your current skills</li>
                  <li>💡 Identify career interests</li>
                  <li>⚙️ Set learning preferences</li>
                  <li>🎯 Get matched with courses</li>
                </ul>
              </div>
            )}
          </div>
          
          <div className="card-actions">
            {assessmentStatus?.assessment_complete ? (
              <Link to="/career/assessment" className="btn-secondary">
                🔄 Retake Assessment
              </Link>
            ) : (
              <Link to="/career/assessment" className="btn-primary">
                🚀 Start Assessment
              </Link>
            )}
          </div>
        </div>

        {/* Recommendations Card */}
        <div className="dashboard-card recommendations-card">
          <div className="card-header">
            <h2>🎯 Top Recommendations</h2>
            {recommendations.length > 0 && (
              <Link to="/career/recommendations" className="view-all-link">
                View All ({assessmentStatus?.recommendations_count})
              </Link>
            )}
          </div>
          
          <div className="card-content">
            {recommendations.length > 0 ? (
              <div className="recommendations-preview">
                {recommendations.map((rec, index) => (
                  <div key={rec.id} className="recommendation-item">
                    <div className="rec-info">
                      <h4>{rec.course_name}</h4>
                      <div className="rec-meta">
                        <span className="match-score">{Math.round(rec.overall_match_score)}% match</span>
                        <span className="campus">{rec.campus_name}</span>
                        <span className="duration">{rec.course_duration} months</span>
                      </div>
                      <div className="rec-passports" aria-label="Passport pathway">
                        {getRecommendationPassports(rec).slice(0, 2).map(passport => (
                          <span className={`rec-passport rec-passport--${passport.tone}`} key={passport.id}>
                            {passport.title}
                          </span>
                        ))}
                      </div>
                      <div className="rec-reasons">
                        {rec.recommendation_reasons && rec.recommendation_reasons.length > 0 && (
                          <span className="top-reason">
                            💡 {rec.recommendation_reasons[0]}
                          </span>
                        )}
                      </div>
                    </div>
                    <Link to={`/courses/${rec.course}`} className="btn-outline-small">
                      View
                    </Link>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-recommendations">
                <p>Complete your career assessment to see personalized course recommendations.</p>
              </div>
            )}
          </div>
          
          {recommendations.length > 0 && (
            <div className="card-actions">
              <Link to="/career/recommendations" className="btn-primary">
                View All Recommendations
              </Link>
            </div>
          )}
        </div>

        {/* Quick Actions Card */}
        <div className="dashboard-card actions-card">
          <div className="card-header">
            <h2>⚡ Quick Actions</h2>
          </div>
          
          <div className="card-content">
            <div className="action-buttons">
              <Link to="/career/assessment" className="action-btn">
                <div className="action-icon">📋</div>
                <div className="action-text">
                  <strong>Career Assessment</strong>
                  <span>Discover your ideal career path</span>
                </div>
              </Link>
              
              <Link to="/career/recommendations" className="action-btn">
                <div className="action-icon">🎯</div>
                <div className="action-text">
                  <strong>Course Recommendations</strong>
                  <span>View personalized suggestions</span>
                </div>
              </Link>
              
              <Link to="/courses" className="action-btn">
                <div className="action-icon">📚</div>
                <div className="action-text">
                  <strong>Browse All Courses</strong>
                  <span>Explore training programs</span>
                </div>
              </Link>
              
              <Link to="/career/comparisons" className="action-btn">
                <div className="action-icon">📊</div>
                <div className="action-text">
                  <strong>Compare Courses</strong>
                  <span>Side-by-side analysis</span>
                </div>
              </Link>
            </div>
          </div>
        </div>

        {/* Saved Comparisons Card */}
        <div className="dashboard-card comparisons-card">
          <div className="card-header">
            <h2>📊 Saved Comparisons</h2>
            {savedComparisons.length > 0 && (
              <Link to="/career/comparisons" className="view-all-link">
                View All
              </Link>
            )}
          </div>
          
          <div className="card-content">
            {savedComparisons.length > 0 ? (
              <div className="comparisons-list">
                {savedComparisons.map((comparison) => (
                  <div key={comparison.id} className="comparison-item">
                    <div className="comparison-info">
                      <h4>{comparison.name}</h4>
                      <div className="comparison-meta">
                        <span>{comparison.courses_count} courses</span>
                        <span>{new Date(comparison.updated_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <Link 
                      to="/career/compare" 
                      state={{ courseIds: comparison.courses }}
                      className="btn-outline-small"
                    >
                      View
                    </Link>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-comparisons">
                <p>No saved comparisons yet. Compare courses to save them for later reference.</p>
              </div>
            )}
          </div>
        </div>

        {/* Career Insights Card */}
        <div className="dashboard-card insights-card">
          <div className="card-header">
            <h2>💡 Career Insights</h2>
          </div>
          
          <div className="card-content">
            <div className="insights-list">
              <div className="insight-item">
                <div className="insight-icon">📈</div>
                <div className="insight-text">
                  <strong>High-Demand Skills</strong>
                  <p>Technology and digital skills are in high demand across all industries.</p>
                </div>
              </div>
              
              <div className="insight-item">
                <div className="insight-icon">🎓</div>
                <div className="insight-text">
                  <strong>Continuous Learning</strong>
                  <p>Regular upskilling increases employment opportunities by 40%.</p>
                </div>
              </div>
              
              <div className="insight-item">
                <div className="insight-icon">🌟</div>
                <div className="insight-text">
                  <strong>Soft Skills Matter</strong>
                  <p>Communication and teamwork skills are valued by 85% of employers.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Card */}
        <div className="dashboard-card progress-card">
          <div className="card-header">
            <h2>📊 Your Progress</h2>
          </div>
          
          <div className="card-content">
            <div className="progress-items">
              <div className="progress-item">
                <div className="progress-label">Assessment Completion</div>
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ 
                      width: assessmentStatus?.assessment_complete ? '100%' : '0%' 
                    }}
                  ></div>
                </div>
                <div className="progress-text">
                  {assessmentStatus?.assessment_complete ? 'Complete' : 'Not Started'}
                </div>
              </div>
              
              <div className="progress-item">
                <div className="progress-label">Recommendations Generated</div>
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ 
                      width: assessmentStatus?.recommendations_count > 0 ? '100%' : '0%' 
                    }}
                  ></div>
                </div>
                <div className="progress-text">
                  {assessmentStatus?.recommendations_count || 0} courses
                </div>
              </div>
              
              <div className="progress-item">
                <div className="progress-label">Courses Compared</div>
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ 
                      width: savedComparisons.length > 0 ? '100%' : '0%' 
                    }}
                  ></div>
                </div>
                <div className="progress-text">
                  {savedComparisons.length} comparisons
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CareerDashboard;
